import Vue from "vue";
import router from "@/router/index.js";
import { i18n } from "@/locales/i18n.js";

const auth = Vue.prototype.$firebase.auth();
const ADMIN_EMAIL_LINK_STORAGE_KEY = "madeall.admin.email-link";

function toNumber(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") return Number(value);
  return NaN;
}

function hasAdminAccess(claims = {}) {
  if (!claims || typeof claims !== "object") return false;
  if (claims.admin === true) return true;

  const level = toNumber(claims.level);
  if (Number.isFinite(level) && level < 3) return true;

  if (claims.role === "admin" || claims.role === "staff") return true;
  if (
    typeof claims.roles === "string" &&
    (claims.roles === "admin" || claims.roles === "staff")
  ) {
    return true;
  }
  if (
    Array.isArray(claims.roles) &&
    (claims.roles.includes("admin") || claims.roles.includes("staff"))
  ) {
    return true;
  }

  return false;
}

function getEmailLinkUrl() {
  return `${window.location.origin}/login`;
}

function readStoredEmailLinkState() {
  if (typeof window === "undefined") return null;

  const rawValue = window.localStorage.getItem(ADMIN_EMAIL_LINK_STORAGE_KEY);
  if (!rawValue) return null;

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    window.localStorage.removeItem(ADMIN_EMAIL_LINK_STORAGE_KEY);
    return null;
  }
}

function writeStoredEmailLinkState(state) {
  if (typeof window === "undefined") return;

  if (!state) {
    window.localStorage.removeItem(ADMIN_EMAIL_LINK_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(
    ADMIN_EMAIL_LINK_STORAGE_KEY,
    JSON.stringify(state),
  );
}

function getFriendlyEmailLinkError(error, mode) {
  if (error?.response?.data && typeof error.response.data === "string") {
    return new Error(error.response.data);
  }

  switch (error?.code) {
    case "auth/operation-not-allowed":
      return new Error(
        "Firebase Authentication에서 Email Link 로그인이 활성화되어 있지 않습니다.",
      );
    case "auth/unauthorized-continue-uri":
    case "auth/invalid-continue-uri":
      return new Error(
        "현재 도메인이 Firebase 인증 허용 도메인에 등록되어 있지 않습니다.",
      );
    case "auth/invalid-email":
    case "auth/missing-email":
      return new Error("올바른 이메일 주소를 입력해주세요.");
    case "auth/expired-action-code":
      return new Error("로그인 링크가 만료되었습니다. 새 링크를 다시 받아주세요.");
    case "auth/invalid-action-code":
      return new Error("유효하지 않은 로그인 링크입니다. 가장 최근 이메일의 링크를 사용해주세요.");
    case "auth/user-disabled":
      return new Error("비활성화된 계정입니다. 관리자에게 문의해주세요.");
    case "auth/network-request-failed":
      return new Error(
        mode === "send"
          ? "네트워크 연결을 확인하고 로그인 링크를 다시 요청해주세요."
          : "네트워크 연결을 확인하고 로그인을 다시 시도해주세요.",
      );
    default:
      if (error instanceof Error && error.message) {
        return error;
      }

      return new Error(
        mode === "send"
          ? "로그인 링크 전송에 실패했습니다."
          : "이메일 링크 로그인에 실패했습니다.",
      );
  }
}
function toLegacyAdminLevel(claims = {}) {
  if (typeof claims.level === "number") return claims.level;

  switch (claims.role) {
    case "developer":
      return 0;
    case "admin":
      return 1;
    case "staff":
      return 2;
    default:
      return 3;
  }
}

function toDateValue(value) {
  if (!value) return value;
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date;
  }
  if (value._seconds) {
    return new Date(value._seconds * 1000);
  }
  return value;
}

function toRecord(value) {
  return value && typeof value === "object" ? value : null;
}

function getNestedValue(source, path) {
  let current = source;

  for (const key of path) {
    const record = toRecord(current);
    if (!record || !(key in record)) return undefined;
    current = record[key];
  }

  return current;
}

function getPhoneAdapter(userData = {}) {
  const phoneRecord = toRecord(userData.phoneNumber);
  const nestedE164 =
    typeof phoneRecord?.e164 === "string" ? phoneRecord.e164.trim() : "";
  const nestedCountryCode =
    typeof phoneRecord?.countryIsoCode === "string"
      ? phoneRecord.countryIsoCode.trim().toUpperCase()
      : "";
  const flatCountryCode =
    typeof userData.countryCode === "string" ? userData.countryCode.trim() : "";
  const flatPhoneNumber =
    typeof userData.phoneNumber === "string" ? userData.phoneNumber.trim() : "";

  let phoneNumber = flatPhoneNumber.replace(/[^\d]/g, "");
  if (!phoneNumber && nestedE164) {
    const digits = nestedE164.replace(/[^\d]/g, "");
    phoneNumber =
      digits.startsWith("82") && digits.length >= 10 ? `0${digits.slice(2)}` : digits;
  }

  let countryCode = flatCountryCode;
  if (countryCode === "KR") countryCode = "+82";
  if (countryCode === "US") countryCode = "+1";
  if (!countryCode) {
    if (nestedCountryCode === "KR") countryCode = "+82";
    else if (nestedCountryCode === "US") countryCode = "+1";
  }
  if (!countryCode && nestedE164.startsWith("+82")) countryCode = "+82";
  if (!countryCode && nestedE164.startsWith("+1")) countryCode = "+1";
  if (!countryCode) countryCode = "+82";

  const phoneNumberE164 =
    nestedE164 ||
    (phoneNumber
      ? `${countryCode}${phoneNumber.startsWith("0") ? phoneNumber.slice(1) : phoneNumber}`
      : "");

  return {
    phoneNumber,
    countryCode,
    phoneNumberE164,
  };
}

function adaptUserData(userData = {}, claims = {}) {
  const phoneAdapter = getPhoneAdapter(userData);
  const profilePhotoURL = getNestedValue(userData, ["profile", "photoURL"]);
  const adminExpiresAt = toDateValue(userData.adminExpiresAt);

  return {
    ...userData,
    photoURL:
      typeof userData.photoURL === "string" && userData.photoURL
        ? userData.photoURL
        : typeof profilePhotoURL === "string"
        ? profilePhotoURL
        : "",
    displayName:
      typeof userData.displayName === "string" && userData.displayName
        ? userData.displayName
        : getNestedValue(userData, ["profile", "name", "ko"]) || "",
    nickName:
      typeof userData.nickName === "string" && userData.nickName
        ? userData.nickName
        : getNestedValue(userData, ["profile", "name", "ko"]) || "",
    profileNameEn:
      typeof userData.profileNameEn === "string" && userData.profileNameEn
        ? userData.profileNameEn
        : getNestedValue(userData, ["profile", "name", "en"]) || "",
    countryCode: phoneAdapter.countryCode,
    phoneNumber: phoneAdapter.phoneNumber,
    phoneNumberE164: phoneAdapter.phoneNumberE164,
    adminExpiresAt,
    createdAt: toDateValue(userData.createdAt),
    updatedAt: toDateValue(userData.updatedAt),
    visitedAt: toDateValue(userData.visitedAt),
    lastLoginAt: toDateValue(userData.lastLoginAt),
    level: toLegacyAdminLevel(claims),
    role: claims.role || userData.role || "",
  };
}

export default {
  async getCountry() {
    return new Promise((resolve, reject) => {
      Vue.prototype.$axios
        .get("auth/countryCode")
        .then((v) => {
          return resolve(v);
        })
        .catch((e) => {
          return reject(e);
        });
    });
  },

  async signInWithGoogle(keepSignIn) {
    const provider = new Vue.prototype.$firebase.auth.GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    auth.languageCode = "ko";

    await auth.setPersistence(
      keepSignIn
        ? Vue.prototype.$firebase.auth.Auth.Persistence.LOCAL
        : Vue.prototype.$firebase.auth.Auth.Persistence.SESSION,
    );

    const result = await auth.signInWithPopup(provider);
    const tokenResult = await result.user.getIdTokenResult(true);

    return {
      user: result.user,
      isAdmin: hasAdminAccess(tokenResult.claims),
    };
  },

  isAdminEmailLink(currentUrl) {
    if (typeof window === "undefined") return false;
    return auth.isSignInWithEmailLink(currentUrl || window.location.href);
  },

  getStoredAdminEmailLinkState() {
    return readStoredEmailLinkState();
  },

  async sendAdminSignInLink(email, keepSignIn) {
    const normalizedEmail = String(email || "").trim().toLowerCase();

    if (!normalizedEmail) {
      throw new Error("이메일을 입력해주세요.");
    }

    try {
      await Vue.prototype.$axios.post("auth/sendAdminSignInLink", {
        email: normalizedEmail,
        continueUrl: getEmailLinkUrl(),
      });

      writeStoredEmailLinkState({
        email: normalizedEmail,
        keepSignIn,
      });

      return { email: normalizedEmail };
    } catch (error) {
      throw getFriendlyEmailLinkError(error, "send");
    }
  },

  async completeAdminEmailLinkSignIn(options = {}) {
    const currentUrl = options.currentUrl || window.location.href;
    const storedState = readStoredEmailLinkState();
    const resolvedEmail =
      String(options.email || "").trim() ||
      String(storedState?.email || "").trim();
    const keepSignIn =
      typeof options.keepSignIn === "boolean"
        ? options.keepSignIn
        : storedState?.keepSignIn ?? true;

    if (!auth.isSignInWithEmailLink(currentUrl)) {
      throw new Error("이메일 링크 로그인 요청이 아닙니다.");
    }

    if (!resolvedEmail) {
      throw new Error("로그인에 사용할 이메일을 다시 입력해주세요.");
    }

    try {
      await auth.setPersistence(
        keepSignIn
          ? Vue.prototype.$firebase.auth.Auth.Persistence.LOCAL
          : Vue.prototype.$firebase.auth.Auth.Persistence.SESSION,
      );

      const result = await auth.signInWithEmailLink(
        resolvedEmail,
        currentUrl,
      );
      const tokenResult = await result.user.getIdTokenResult(true);
      writeStoredEmailLinkState(null);

      return {
        user: result.user,
        email: resolvedEmail,
        isAdmin: hasAdminAccess(tokenResult.claims),
      };
    } catch (error) {
      throw getFriendlyEmailLinkError(error, "complete");
    }
  },


  /*
   * 梨꾨꼸??Plugin Reboot
   */
  rebootChannelIo() {
    if (auth.currentUser) {
      window.ChannelIO(
        "boot",
        {
          pluginKey: "e6541c37-05a3-4119-8e7f-88b9c21a509c",
          memberId: auth.currentUser.uid,
          profile: {
            name: auth.currentUser.displayName,
            mobileNumber: auth.currentUser.phoneNumber,
          },
        },
        function onBoot(error, user) {
          if (error) {
            return;
          } else {
            window.channelUser = user;
          }
        }
      );
    } else {
      window.ChannelIO("shutdown");
    }
  },


  waitForCurrentUser(timeout = 5000) {
    return new Promise((resolve, reject) => {
      const intervalTime = 100;
      let elapsed = 0;

      const check = setInterval(() => {
        if (auth.currentUser) {
          clearInterval(check);
          resolve(auth.currentUser);
        } else if (elapsed > timeout) {
          clearInterval(check);
          reject(new Error("Timeout waiting for auth.currentUser"));
        }
        elapsed += intervalTime;
      }, intervalTime);
    });
  },

  async finishLogin() {
    await auth.currentUser.reload();
    this.rebootChannelIo();
    return router
      .push({ name: "Home", query: { confirm: true } })
      .then(() => window.scrollTo(0, 0));
  },

  resetPasswordByPhone(phone) {
    return new Promise((resolve, reject) => {
      Vue.prototype.$axios
        .patch("auth/getUserWithPhone", { phone: phone })
        .then((res) => {
          //uid: kakao??naver?쇨꼍??
          //?먮뒗 provider媛 google?쇨꼍??
          if (
            res.data.uid.includes("kakao") ||
            res.data.uid.includes("naver") ||
            res.data.providerData[0].providerId == "google.com"
          ) {
            Vue.toasted.global.error(i18n.t("resetPassword.sns_account"));
            return reject();
          }

          this.resetPasswordByEmail(res.data.email)
            .then(() => {
              return resolve();
            })
            .catch((e) => {
              return reject(e);
            });
        })
        .catch((e) => {
          Vue.toasted.global.error(i18n.t("resetPassword.no_account_exist"));
          return reject(e);
        });
    });
  },

  resetPasswordByEmail(email) {
    return new Promise((resolve, reject) => {
      auth
        .sendPasswordResetEmail(email)
        .then(() => {
          Vue.toasted.global.notice(i18n.t("resetPassword.email_sended"));
          return resolve();
        })
        .catch((e) => {
          switch (e.code) {
            case "auth/invalid-email":
              Vue.toasted.global.error(i18n.t("resetPassword.invalid_email"));
              break;
            case "auth/email-already-in-use":
              Vue.toasted.global.error(
                i18n.t("resetPassword.email_already_in_use")
              );
              break;
            case "auth/user-not-found":
              Vue.toasted.global.error(i18n.t("resetPassword.user_not_found"));
              break;
            case "auth/missing-email":
              Vue.toasted.global.error(i18n.t("resetPassword.missing_email"));
              break;
            default:
              Vue.toasted.global.error(i18n.t("resetPassword.unknwon_error"));
              break;
          }
          return reject(e);
        });
    });
  },

  getStoreUserData() {
    return new Promise((resolve, reject) => {
      this.waitForCurrentUser()
        .then((user) => {
          Vue.prototype.$axios
            .get(`users/${user.uid}`)
            .then(async (res) => {
              const { claims } = await user.getIdTokenResult(true);
              resolve(adaptUserData(res.data, claims));
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};


